import React, { useEffect, useState } from 'react'
import { styled } from '@mui/material/styles'
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp'
import Accordion from '@mui/material/Accordion'
import PropTypes from 'prop-types'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import { Box, IconButton } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import config from '../config/config'
import {
  clearAllSelectedFeatures,
  setPopupExpanded,
  setSelectedFeature,
  setShareableURL,
  setShowConfirmDeleteModal,
  setShowPopup,
  setShowToolbox
} from '../slices/appSlice'
import { setDrawerOpen, setProjectInputStep } from '../slices/mainTool'
import {
  collapseLegendAndBaseMap,
  removeSelectedSegment,
  zoomToSelectedFeature
} from '../controller/esriMapController'
import CustomButton from './CustomButton'
import { LightTooltip } from './CriteriaSelection/ModalCriteria'
import { modalTiers } from '../config/generalInputs.config'
import { themeConfig } from '../config/theme.config'
import {
  PEDESTRIAN,
  TRANSIT,
  FREIGHT,
  AUTO,
  BICYCLE
} from '../constants/modalTiers'
import PedestrianIcon from '../icons/PedestrianIcon'
import AutoIcon from '../icons/AutoIcon'
import BicycleIcon from '../icons/BicycleIcon'
import TransitIcon from '../icons/TransitIcon'
import FreightIcon from '../icons/FreightIcon'
import { NA } from '../constants/scores'
import {
  AUTO_TIER,
  BIKE_TIER,
  FREIGHT_TIER,
  PED_TIER,
  ST_NAME,
  ST_TYPE,
  TRANSIT_TIER
} from '../constants/layerFieldNames'

export const CustomAccordion = styled((props) => (
  <Accordion
    disableGutters={true}
    elevation={0}
    {...props}
    expanded={props.expandaccordion === 'true'}
  />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,

  '&:not(:last-child)': {
    borderBottom: 0
  },
  '&:before': {
    display: 'none'
  },
  '&:first-of-type': {
    borderTopLeftRadius: '20px !important',
    borderTopRightRadius: '20px !important'
  },
  '&:last-child': {
    borderBottomLeftRadius: '20px !important',
    borderBottomRightRadius: '20px !important'
  }
}))

export const CustomAccordionSummary = styled((props) => (
  <AccordionSummary {...props} />
))(({ theme }) => ({
  backgroundColor: config.themeConfig.semcogBlue1,
  borderTopLeftRadius: '15px !important',
  borderTopRightRadius: '15px !important',
  color: 'white',
  flexDirection: 'row',
  display: 'flex',
  '& .AccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)'
  },
  '& .AccordionSummary-content': {
    marginLeft: theme.spacing(1)
  }
}))

export const CustomAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
  backgroundColor: '#EDF1F5',
  borderBottomLeftRadius: '15px !important',
  borderBottomRightRadius: '15px !important'
}))

const AttributeFields = ({ field, value, marginLeft }) => {
  return (
    <Box sx={{ marginLeft }}>
      <Typography sx={{ color: '#676869', fontSize: '12px' }}>
        {field}
      </Typography>
      <Typography sx={{ color: 'black', fontWeight: 700 }}>{value}</Typography>
    </Box>
  )
}

export default function CustomPopup({ attributes }) {
  const dispatch = useDispatch()

  const { shareableURL, popupExpanded } = useSelector((state) => state.appSlice)

  const handleChange = () => {
    dispatch(setPopupExpanded(!popupExpanded))
    !popupExpanded && dispatch(setDrawerOpen(false))
  }

  const handleClose = () => {
    // If segmetns were cleared from the exsisting list and its a shareable URL, then set sahreable to false
    if (shareableURL) {
      dispatch(setShareableURL(false))
    }
    // On pop-up close , remove selected segments
    dispatch(setShowConfirmDeleteModal(true))
    // removeSelectedSegment()
  }

  function getIcon(name) {
    switch (name) {
      case PEDESTRIAN:
        return <PedestrianIcon width="30" height="35" fill="white" />
      case AUTO:
        return <AutoIcon width="30" height="35" fill="white" />
      case BICYCLE:
        return <BicycleIcon width="30" height="35" fill="white" />
      case TRANSIT:
        return <TransitIcon width="30" height="35" fill="white" />
      case FREIGHT:
        return <FreightIcon width="30" height="35" fill="white" />

      default:
    }
  }

  function getTierValue(name, attributes) {
    switch (name) {
      case PEDESTRIAN:
        return attributes?.[PED_TIER]
      case AUTO:
        return attributes?.[AUTO_TIER]
      case BICYCLE:
        return attributes?.[BIKE_TIER]
      case TRANSIT:
        return attributes?.[TRANSIT_TIER]
      case FREIGHT:
        return attributes?.[FREIGHT_TIER]

      default:
    }
  }

  return (
    <Box
      sx={{
        width: '450px',
        position: 'absolute',

        zIndex: 100002,
        margin: '15px'
      }}
    >
      <CustomAccordion expandaccordion={popupExpanded ? 'true' : 'false'}>
        <CustomAccordionSummary
          aria-controls="panel1d-content"
          id="panel1d-header"
        >
          <Typography
            sx={{
              flexGrow: 6,
              color: 'white',
              alignSelf: 'center',
              fontWeight: 700,
              fontSize: '20px'
            }}
          >
            {`Current Project: ${attributes?.[ST_NAME]} ${attributes?.[ST_TYPE]}`}
          </Typography>
          <IconButton onClick={handleChange} sx={{ color: 'white' }}>
            <ArrowForwardIosSharpIcon
              sx={
                popupExpanded
                  ? { transform: 'rotate(-90deg)', fontSize: '1.3rem' }
                  : { transform: 'rotate(90deg)', fontSize: '1.3rem' }
              }
            />
          </IconButton>
          <LightTooltip title="Clears all selected segments" placement="right">
            <IconButton sx={{ color: 'white' }} onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </LightTooltip>
        </CustomAccordionSummary>
        <CustomAccordionDetails>
          <Box>
            <AttributeFields field="Context" value={attributes?.Context2} />
            <Box sx={{ display: 'flex', mt: '0.5em' }}>
              <AttributeFields
                field="Street Name"
                value={attributes?.[ST_NAME] + ' ' + attributes?.[ST_TYPE]}
              />
              {/* <AttributeFields
                field="Street Type"
                value={attributes?.FETYPE}
                marginLeft="80px"
              /> */}
            </Box>
            <Box sx={{ mt: '0.5em' }}>
              <Typography sx={{ color: '#676869', fontSize: '12px' }}>
                Modal Tiers
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
                  width: '50%',
                  gap: '10px',
                  mt: '0.2em'
                }}
              >
                {modalTiers.map((tier, index) => {
                  return (
                    <Box
                      key={`${tier.name}-popup-tier`}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '5px'
                      }}
                    >
                      <Box
                        sx={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '50%',
                          backgroundColor: themeConfig.semcogBlue1,
                          // padding: '1em',

                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}
                      >
                        {getIcon(tier.name)}
                      </Box>
                      <Typography
                        sx={{
                          color: 'black',
                          fontWeight: 700,
                          textAlign: 'center'
                        }}
                      >
                        {getTierValue(tier.name, attributes) || NA}
                      </Typography>
                    </Box>
                  )
                })}
              </Box>
            </Box>
            <Box
              sx={
                shareableURL
                  ? { display: 'flex', justifyContent: 'flex-end' }
                  : { display: 'flex', justifyContent: 'space-between' }
              }
            >
              {!shareableURL && (
                <CustomButton
                  handleNext={() => {
                    zoomToSelectedFeature()
                  }}
                  btnLabel="Zoom To Feature"
                  justifyContent="right"
                  showZoomIcon={true}
                  fontWeight={700}
                />
              )}
              <CustomButton
                handleNext={() => {
                  dispatch(setProjectInputStep(0))
                  dispatch(setDrawerOpen(true))
                  // Collapse legent
                  collapseLegendAndBaseMap()
                }}
                btnLabel="SET UP PROJECT"
                justifyContent="right"
                withIcon={true}
                fontWeight={700}
              />
            </Box>
          </Box>
        </CustomAccordionDetails>
      </CustomAccordion>
    </Box>
  )
}

CustomPopup.propTypes = {
  attributes: PropTypes.object
}
AttributeFields.propTypes = {
  field: PropTypes.string.isRequired,
  value: PropTypes.string,
  marginLeft: PropTypes.string
}
